import { ChangeEvent, useEffect, useState } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers'
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay'
import { isValid } from 'date-fns'
import { isEmpty } from 'lodash'

import { DefaultElementProps } from 'common/interfaces'
import { textFieldPickerStyle } from 'common/styles'
import { SizingContainer } from 'components/SizingContainer'
import { useTemplate } from 'utils/hook'
import useSetValueAsTZFormat from 'utils/useSetValueAsTZFormat'
import validationsToRules from 'utils/validationsToRules'

import { DatePicker as DatePickerProps } from './interface'

const datePickerRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/
const pickerYearButtonSelector = '.MuiPickersYear-yearButton'
const muiSelectedClass = 'Mui-selected'

function getDatePickerOwnerId(name: string): string {
  return `date-picker-${name.replace(/[^a-zA-Z0-9_-]/g, '-')}-picker`
}

function getOpenPickerRoot(name: string): Element | null {
  return document.getElementById(getDatePickerOwnerId(name))
}

export function annotatePickerYearButtons(container: ParentNode): void {
  container.querySelectorAll<HTMLButtonElement>(pickerYearButtonSelector).forEach((button) => {
    const year = button.textContent?.trim()
    if (year === undefined || year === '') {
      return
    }

    const isSelected = button.classList.contains(muiSelectedClass)
    button.setAttribute('aria-label', isSelected ? `Year ${year}, selected` : `Year ${year}`)

    if (isSelected) {
      button.setAttribute('aria-pressed', 'true')
    } else {
      button.removeAttribute('aria-pressed')
    }
  })
}

function AccessiblePickersDay(props: PickersDayProps<Date>): JSX.Element {
  const { day, selected, today, ...other } = props
  const dateLabel = day.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
  const stateLabel = [today === true ? 'today' : undefined, selected === true ? 'selected' : undefined]
    .filter(Boolean)
    .join(', ')
  const label = stateLabel === '' ? dateLabel : `${dateLabel}, ${stateLabel}`

  return (
    <PickersDay
      {...other}
      day={day}
      selected={selected}
      today={today}
      aria-label={label}
      aria-current={today === true ? 'date' : undefined}
      aria-pressed={selected === true ? 'true' : undefined}
    />
  )
}

export default function DatePicker({
  name,
  size,
  label,
  onFocus,
  onChange,
  validations,
  defaultValue,
  disableFuture,
}: DatePickerProps & DefaultElementProps): JSX.Element {
  const render = useTemplate()

  const { control, setValue, setError, clearErrors } = useFormContext()
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    rules: validationsToRules(validations),
    defaultValue: render(defaultValue ? new Date(defaultValue).toISOString() : ''),
  })

  const onChangeDatePicker = (newValue: Date | null): void => {
    if (isEmpty(error)) {
      if (newValue) {
        const dateOnly = new Date(newValue.getFullYear(), newValue.getMonth(), newValue.getDate())
        setValue(name, dateOnly.toISOString())
      } else {
        setValue(name, newValue)
      }

      return
    }

    setValue(name, newValue)
  }

  const onChangeDatePickerField = (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ): void => {
    const { value } = e.target
    if (datePickerRegex.test(value)) {
      const valueInISOString = new Date(value).toISOString()

      e.target.value = valueInISOString
      field.onChange(e)

      onChange?.({ value: valueInISOString })

      setValue(name, valueInISOString)

      clearErrors(name)

      return
    }

    if (value !== '') {
      setError(name, { message: 'Your date in wrong format' })
    } else {
      clearErrors(name)
    }
  }

  useSetValueAsTZFormat(field)

  const getDateValue = (val: unknown): Date | null => {
    if (!val) return null
    if (val instanceof Date) return isValid(val) ? val : null
    if (typeof val === 'string') {
      const date = new Date(val)
      return isValid(date) ? date : null
    }

    return null
  }

  const [open, setOpen] = useState<boolean>(false)

  const showPicker = (): void => {
    setOpen(true)
  }

  const hidePicker = (): void => {
    setOpen(false)
  }

  const pickerOwnerId = getDatePickerOwnerId(name)

  useEffect(() => {
    if (!open) {
      return undefined
    }

    const annotateCurrentPickerYears = (): void => {
      const pickerRoot = getOpenPickerRoot(name)

      if (pickerRoot !== null) {
        annotatePickerYearButtons(pickerRoot)
      }
    }

    annotateCurrentPickerYears()

    const observer = new MutationObserver(annotateCurrentPickerYears)
    observer.observe(document.body, { childList: true, subtree: true })

    return () => observer.disconnect()
  }, [open, field.value, name])

  return (
    <SizingContainer className="date-picker-wrap" size={size} minWidth={150}>
      <MuiDatePicker
        {...field}
        value={getDateValue(field.value)}
        disableFuture={disableFuture}
        label={label}
        onChange={onChangeDatePicker}
        onOpen={showPicker}
        onClose={hidePicker}
        open={open}
        slots={{
          day: AccessiblePickersDay,
        }}
        slotProps={{
          popper: {
            id: pickerOwnerId,
          },
          mobilePaper: {
            id: pickerOwnerId,
          },
          textField: (ownerState) => {
            const errorMessageId = `${name}-helper-text`
            return {
              id: name,
              required: validations?.required?.value,
              sx: textFieldPickerStyle,
              fullWidth: true,
              onClick: showPicker,
              onFocus,
              onChange: onChangeDatePickerField,
              error: error !== undefined,
              helperText: error?.message,
              inputProps: {
                ...ownerState.inputProps,
                readOnly: true,
                'aria-invalid': error !== undefined,
                'aria-describedby': error !== undefined ? errorMessageId : undefined,
              },
              FormHelperTextProps: {
                id: errorMessageId,
                role: error !== undefined ? 'alert' : undefined,
              },
            }
          },
        }}
      />
    </SizingContainer>
  )
}
